import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { listIds, matchPhotos, fileUrl, errMsg } from "../lib/api";

export const candidateUrls = (d) => [
  ...(d.gbif?.media || []).slice(0, 6).map((m) => m.photo),
  ...(d.inaturalist?.observations || []).slice(0, 6).map((o) => o.photo),
  ...(d.inaturalist?.taxon_photos || []).slice(0, 4).map((p) => p.photo),
].filter(Boolean);

export const PhotoMatch = ({ d, refId, setRefId, scores, setScores, setKernel }) => {
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { listIds({ limit: 30 }).then(setRecent).catch(() => {}); }, [refId]);
  const ref = recent.find((x) => x.id === refId);

  const run = async () => {
    setBusy(true); setKernel("busy");
    try {
      const r = await matchPhotos(refId, candidateUrls(d));
      setScores(Object.fromEntries(r.scores.map((s) => [s.url, s])));
      if ((r.scores[0]?.score ?? 0) >= 40) toast.success("Best matching photos highlighted");
      else toast.warning("No close match found for your moth");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); setKernel("idle"); }
  };
  const best = scores ? Object.values(scores).sort((a, b) => b.score - a.score).slice(0, 3) : [];

  return (
    <div className="space-y-3" data-testid="photo-match-panel">
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 text-xs font-mono min-w-0 max-w-full">
          <span className="text-muted-foreground shrink-0">my_photo =</span>
          <select data-testid="photo-match-ref-select" value={refId || ""} onChange={(e) => { setRefId(e.target.value || null); setScores(null); }} className="h-9 rounded border bg-card px-2 text-sm font-mono min-w-0 w-full sm:w-72">
            <option value="">— choose an uploaded moth —</option>
            {recent.map((x) => <option key={x.id} value={x.id}>{x.predictions?.[0]?.scientific_name} · {new Date(x.created_at).toLocaleDateString()}</option>)}
          </select>
        </label>
        <Button data-testid="photo-match-run-button" size="sm" disabled={!refId || busy} onClick={run}>
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}{busy ? "Comparing photos…" : "Find best matches"}
        </Button>
      </div>
      {ref && (
        <div className="flex flex-wrap gap-3 items-start">
          <figure className="w-32">
            <img data-testid="photo-match-ref-image" src={fileUrl(ref.image_path)} alt="your moth" className="w-32 h-32 object-cover rounded border-2 border-primary" />
            <figcaption className="text-[10px] font-mono mt-1 text-muted-foreground">your specimen</figcaption>
          </figure>
          {best.map((b, i) => (
            <figure key={b.url} className="w-32" data-testid={`photo-match-best-${i}`}>
              <img src={b.url} alt="" className={`w-32 h-32 object-cover rounded ${b.score >= 40 ? "ring-2 ring-emerald-500" : "opacity-70 border"}`} />
              <figcaption className="text-[10px] font-mono mt-1"><b>#{i + 1} · {b.score}%</b> <span className="text-muted-foreground">{b.reason}</span></figcaption>
            </figure>
          ))}
        </div>
      )}
      {busy && <div className="text-xs font-mono text-muted-foreground">AI is comparing your photo with up to 16 GBIF & iNaturalist photos (~10 s)…</div>}
      {best.length > 0 && best[0].score < 40 && <div data-testid="photo-match-low-warning" className="font-mono text-xs rounded px-3 py-2 bg-[hsl(var(--nb-err-bg))] text-[hsl(var(--nb-err))]">MatchWarning: best score is only {best[0].score}% — your specimen is probably not this species.</div>}
    </div>
  );
};
